import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const V2_THEME_STORAGE_KEY = 'ids_ui_v2_theme';

const V2ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
});

const getStoredTheme = () => {
  try {
    const v = localStorage.getItem(V2_THEME_STORAGE_KEY);
    if (v === 'dark' || v === 'light') return v;
    // No stored preference — follow the OS setting
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  } catch {
    return 'light';
  }
};

/**
 * Applies theme tokens via documentElement data-theme (see themecontrol.css [data-theme="dark"]).
 */
export function V2ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getStoredTheme);

  const applyDomTheme = useCallback((mode) => {
    const root = document.documentElement;
    if (mode === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
  }, []);

  useEffect(() => {
    applyDomTheme(theme);
    return () => {
      document.documentElement.removeAttribute('data-theme');
    };
  }, [theme, applyDomTheme]);

  // When no user preference is stored, keep in sync with OS theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const stored = localStorage.getItem(V2_THEME_STORAGE_KEY);
      if (!stored) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  const setTheme = useCallback((mode) => {
    const next = mode === 'dark' ? 'dark' : 'light';
    setThemeState(next);
    try {
      localStorage.setItem(V2_THEME_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(V2_THEME_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  );

  return (
    <V2ThemeContext.Provider value={value}>{children}</V2ThemeContext.Provider>
  );
}

export function useV2Theme() {
  return useContext(V2ThemeContext);
}

